import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '@/lib/api/client';

// Job Tasks — a job-scoped view of the SAME planner cards the /planner board
// and the worker portals use (schedule_subtasks). One task system everywhere:
// add here, drag on the board, crew checks off from their timesheet.

interface Card {
  id: number;
  title: string;
  description?: string | null;
  status: 'pending' | 'in_progress' | 'completed' | 'blocked';
  start_date: string | null;
  assignee_name?: string | null;
  approval_status?: 'approved' | 'pending' | 'rejected';
  suggested_by_name?: string | null;
  checklist_total?: number;
  checklist_done?: number;
  /** 0 hides the task from the client portal (CR #130). */
  visible_to_client?: number;
  /** CR #196 — set by the server when status flips to completed. */
  completed_at?: string | null;
}
interface ChecklistItem { id: number; text: string; checked: number; checked_at?: string | null; display_order: number }

const day = (s?: string | null) => (s || '').slice(0, 10);
const fmtDay = (d: string) =>
  new Date(d + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

export const JobTasksTab = ({ jobId }: { jobId: string }) => {
  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [newTitle, setNewTitle] = useState('');
  const [newDate, setNewDate] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [busy, setBusy] = useState(false);
  // Sub-task checklists: expanded card id → its items
  const [openCheck, setOpenCheck] = useState<number | null>(null);
  const [checkItems, setCheckItems] = useState<ChecklistItem[]>([]);
  const [newCheck, setNewCheck] = useState('');

  const loadChecklist = async (cardId: number) => {
    setOpenCheck(cardId);
    try {
      const r = await apiClient.get(`/planner/tasks/${cardId}/checklist`);
      setCheckItems(r.data.data ?? []);
    } catch { setCheckItems([]); }
  };
  const addCheckItem = async (cardId: number) => {
    const text = newCheck.trim();
    if (!text) return;
    setNewCheck('');
    try {
      await apiClient.post(`/planner/tasks/${cardId}/checklist`, { text });
      loadChecklist(cardId);
      setCards(cs => cs.map(c => c.id === cardId ? { ...c, checklist_total: (c.checklist_total || 0) + 1 } : c));
    } catch { /* reload reconciles */ }
  };
  const toggleCheckItem = async (cardId: number, item: ChecklistItem) => {
    const next = item.checked ? 0 : 1;
    setCheckItems(is => is.map(i => i.id === item.id ? { ...i, checked: next, checked_at: next ? new Date().toISOString() : null } : i));
    setCards(cs => cs.map(c => c.id === cardId ? { ...c, checklist_done: (c.checklist_done || 0) + (next ? 1 : -1) } : c));
    try { await apiClient.patch(`/planner/checklist/${item.id}`, { checked: !!next }); } catch { loadChecklist(cardId); }
  };
  const removeCheckItem = async (cardId: number, id: number) => {
    setCheckItems(is => is.filter(i => i.id !== id));
    try { await apiClient.delete(`/planner/checklist/${id}`); loadChecklist(cardId); } catch { /* ignore */ }
  };

  const load = useCallback(async () => {
    try {
      const r = await apiClient.get(`/planner/board?job_id=${jobId}`);
      setCards(r.data.data ?? []);
    } catch { /* keep last */ }
    finally { setLoading(false); }
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    const m = new Map<string, Card[]>();
    for (const c of cards) {
      const key = c.start_date ? day(c.start_date) : 'unscheduled';
      const arr = m.get(key) || [];
      arr.push(c);
      m.set(key, arr);
    }
    return [...m.entries()].sort(([a], [b]) =>
      a === 'unscheduled' ? 1 : b === 'unscheduled' ? -1 : a.localeCompare(b));
  }, [cards]);

  const add = async () => {
    const title = newTitle.trim();
    if (!title) return;
    setBusy(true);
    try {
      const today = new Date();
      const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const r = await apiClient.post('/planner/tasks', { job_id: Number(jobId), date: newDate || todayKey, title });
      let card: Card = r.data.data;
      if (!newDate) {
        const moved = await apiClient.patch(`/planner/tasks/${card.id}`, { start_date: null });
        card = moved.data.data ?? { ...card, start_date: null };
      }
      setCards(cs => [...cs, card]);
      setNewTitle('');
    } catch { /* board reload reconciles */ }
    finally { setBusy(false); }
  };

  const patch = async (id: number, body: Record<string, unknown>) => {
    const prev = cards;
    setCards(cs => cs.map(c => c.id === id ? { ...c, ...body } as Card : c));
    try {
      const r = await apiClient.patch(`/planner/tasks/${id}`, body);
      if (r.data.data) setCards(cs => cs.map(c => c.id === id ? r.data.data : c));
    } catch { setCards(prev); }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Delete this task?')) return;
    setCards(cs => cs.filter(c => c.id !== id));
    try { await apiClient.delete(`/planner/tasks/${id}`); } catch { load(); }
  };

  if (loading) return <div className="h-20 animate-pulse rounded-xl bg-gray-100" />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-gray-500">
          Same cards as the planner board and the crew's portal — day-planned tasks get checked off with their hours.
        </p>
        <Link to="/planner" className="flex items-center gap-1 text-xs font-semibold text-[#C77D3F] whitespace-nowrap">
          <span className="material-symbols-outlined text-base">view_kanban</span> Open planner
        </Link>
      </div>

      {/* Add */}
      <div className="flex gap-2 flex-wrap">
        <input
          value={newTitle}
          onChange={e => setNewTitle(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') add(); }}
          placeholder="New task for this job…"
          className="flex-1 min-w-[12rem] border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40"
        />
        <input
          type="date" value={newDate} onChange={e => setNewDate(e.target.value)}
          title="Day (blank = unscheduled)"
          className="border border-gray-300 rounded-lg px-2 py-2 text-sm focus:outline-none"
        />
        <button onClick={add} disabled={busy || !newTitle.trim()}
          className="h-10 px-4 rounded-lg bg-[#C77D3F] text-white text-sm font-bold disabled:opacity-40">
          Add
        </button>
      </div>

      {cards.length === 0 ? (
        <div className="bg-gray-50 rounded-xl border border-gray-100 p-6 text-center text-sm text-gray-400">
          No tasks yet — add one above or plan the week on the board.
        </div>
      ) : (
        groups.map(([key, list]) => (
          <div key={key}>
            <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">
              {key === 'unscheduled' ? 'Unscheduled' : fmtDay(key)}
            </p>
            <div className="divide-y divide-gray-50 border border-gray-100 rounded-xl overflow-hidden">
              {list.map(c => {
                const done = c.status === 'completed';
                const awaiting = c.approval_status === 'pending';
                return (
                  <div key={c.id}>
                  <div className="flex items-center gap-2 px-3 py-2 bg-white">
                    <button
                      onClick={() => patch(c.id, { status: done ? 'pending' : 'completed' })}
                      title={done ? 'Mark not done' : 'Mark done'}
                    >
                      <span className={`material-symbols-outlined text-xl ${done ? 'text-green-600' : awaiting ? 'text-amber-500' : 'text-gray-300 hover:text-green-500'}`}>
                        {done ? 'check_circle' : awaiting ? 'hourglass_top' : 'radio_button_unchecked'}
                      </span>
                    </button>
                    <div className="flex-1 min-w-0">
                      {editingId === c.id ? (
                        <input
                          autoFocus value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          onBlur={() => { setEditingId(null); if (editValue.trim() && editValue.trim() !== c.title) patch(c.id, { title: editValue.trim() }); }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                          className="w-full border border-[#C77D3F] rounded px-2 py-0.5 text-sm focus:outline-none"
                        />
                      ) : (
                        <button onClick={() => { setEditingId(c.id); setEditValue(c.title); }}
                          className={`text-left text-sm font-medium truncate max-w-full ${done ? 'text-gray-400 line-through' : 'text-gray-900'}`}
                          title="Tap to rename">
                          {c.title}
                        </button>
                      )}
                      <p className="text-[10px] text-gray-400 truncate">
                        {awaiting && <span className="text-amber-700 font-semibold">Suggested{c.suggested_by_name ? ` by ${c.suggested_by_name}` : ''} — approve on the board · </span>}
                        {c.assignee_name || 'Whole crew'}
                        {done && c.completed_at && (
                          <span className="text-green-700"> · done {new Date(c.completed_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}</span>
                        )}
                      </p>
                    </div>
                    <button
                      onClick={() => openCheck === c.id ? setOpenCheck(null) : loadChecklist(c.id)}
                      title="Sub-tasks (checklist)"
                      className={`h-7 px-1.5 rounded-md text-[10px] font-bold flex items-center gap-0.5 border ${
                        (c.checklist_total || 0) > 0
                          ? (c.checklist_done === c.checklist_total ? 'bg-green-50 text-green-700 border-green-200' : 'bg-[#FAF6F0] text-[#8a5526] border-[#C77D3F]/30')
                          : 'bg-white text-gray-400 border-gray-200'
                      }`}>
                      <span className="material-symbols-outlined text-[12px] leading-none">checklist</span>
                      {(c.checklist_total || 0) > 0 ? `${c.checklist_done || 0}/${c.checklist_total}` : '+'}
                    </button>
                    {/* CR #130 — share this task with the client portal */}
                    <button
                      onClick={() => patch(c.id, { visible_to_client: c.visible_to_client === 0 ? 1 : 0 })}
                      title={c.visible_to_client === 0
                        ? 'Hidden from the client portal — click to share'
                        : 'Visible in the client portal — click to hide'}
                      className={`h-7 w-7 rounded-md flex items-center justify-center border ${
                        c.visible_to_client === 0
                          ? 'bg-white text-gray-300 border-gray-200 hover:text-gray-500'
                          : 'bg-green-50 text-green-700 border-green-200'
                      }`}>
                      <span className="material-symbols-outlined text-[14px] leading-none">
                        {c.visible_to_client === 0 ? 'visibility_off' : 'visibility'}
                      </span>
                    </button>
                    <input
                      type="date" value={day(c.start_date)}
                      onChange={e => patch(c.id, { start_date: e.target.value || null })}
                      title="Planned day (clear = unscheduled)"
                      className="border border-gray-200 rounded-md px-1.5 py-1 text-xs text-gray-600 focus:outline-none w-32"
                    />
                    <button onClick={() => remove(c.id)} title="Delete"
                      className="h-8 w-8 rounded-lg text-gray-300 hover:text-red-600 hover:bg-red-50 flex items-center justify-center">
                      <span className="material-symbols-outlined text-lg">delete</span>
                    </button>
                  </div>
                  {openCheck === c.id && (
                    <div className="bg-gray-50 border-t border-gray-100 px-3 py-2 pl-10 space-y-1">
                      {checkItems.map(it => (
                        <div key={it.id} className="flex items-center gap-2 group/ci">
                          <input type="checkbox" checked={!!it.checked} onChange={() => toggleCheckItem(c.id, it)} className="accent-[#C77D3F]" />
                          <span className={`text-xs flex-1 ${it.checked ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{it.text}</span>
                          {!!it.checked && it.checked_at && (
                            <span className="text-[10px] text-green-700 flex-shrink-0">{new Date(it.checked_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}</span>
                          )}
                          <button onClick={() => removeCheckItem(c.id, it.id)}
                            className="opacity-0 group-hover/ci:opacity-100 text-gray-300 hover:text-red-500">
                            <span className="material-symbols-outlined text-sm">close</span>
                          </button>
                        </div>
                      ))}
                      <div className="flex items-center gap-1.5">
                        <input value={newCheck} onChange={e => setNewCheck(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') addCheckItem(c.id); }}
                          placeholder="Add sub-task… (e.g. double rim board)"
                          className="flex-1 border border-gray-200 rounded-md px-2 py-1 text-xs bg-white focus:outline-none" />
                        <button onClick={() => addCheckItem(c.id)} disabled={!newCheck.trim()}
                          className="h-6 px-2 rounded-md bg-[#C77D3F] text-white text-[10px] font-bold disabled:opacity-40">Add</button>
                      </div>
                    </div>
                  )}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
};
